import React, { useEffect, useRef } from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  BooleanInput,
  FormDataConsumer,
  ReferenceInput,
  AutocompleteInput,
  required,
  useRecordContext,
  useGetOne,
  useGetList,
  minValue,
  type EditProps,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import NoArrowKeyNumberInput from "@/components/NoArrowKeyNumberInput";
import { formatINR } from "@/utils";
import { BeneficiaryItsAutocomplete } from "./common/BeneficiaryItsAutocomplete";
import { ITSInput } from "./common/FmbContributionsItsInput";
import {
  validateBeneficiaryDisplayName,
  validateContributionFmbOrPeriod,
  validatePositiveContributionTotal,
} from "./common/contribution-form-validators";
import { SeedOverrideAmountFromCalculated } from "./common/seed-override-amount-from-calculated";
import FmbZabihatBookedSlotsInput, {
  transformBookedSlotsForApi,
  validateZabihatBookedSlots,
} from "./common/FmbZabihatBookedSlotsInput";

function ClearTakhmeenWhenNoFmb() {
  const { control, setValue } = useFormContext();
  const fmbId = useWatch({ control, name: "fmbId" });

  useEffect(() => {
    if (!fmbId) {
      setValue("fmbTakhmeenId", null, { shouldDirty: false });
    }
  }, [fmbId, setValue]);

  return null;
}

function TakhmeenHijriSync() {
  const { setValue, control } = useFormContext();
  const fmbId = useWatch({ control, name: "fmbId" });
  const fmbTakhmeenId = useWatch({ control, name: "fmbTakhmeenId" });
  const { data: t } = useGetOne(
    "fmbTakhmeen",
    { id: fmbTakhmeenId },
    { enabled: Boolean(fmbId) && Boolean(fmbTakhmeenId) }
  );

  useEffect(() => {
    if (t?.hijriYearStart != null && fmbTakhmeenId) {
      setValue("hijriYearStart", t.hijriYearStart, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [t?.hijriYearStart, t?.id, fmbTakhmeenId, setValue]);

  return null;
}

const transform = (data: Record<string, unknown>) => {
  const out: Record<string, unknown> = {
    contributionType: data.contributionType,
    beneficiaryItsNo:
      typeof data.beneficiaryItsNo === "string"
        ? data.beneficiaryItsNo.trim()
        : data.beneficiaryItsNo,
    quantity: Number(data.quantity),
    isAmountOverridden: data.isAmountOverridden === true,
    remarks:
      typeof data.remarks === "string" ? data.remarks.trim() || null : (data.remarks ?? null),
  };
  if (data.categoryId) {
    out.categoryId = data.categoryId;
  }
  if (typeof data.beneficiaryName === "string") {
    const bn = data.beneficiaryName.trim();
    if (bn) {
      out.beneficiaryName = bn;
    }
  }
  if (data.fmbId) {
    out.fmbId = data.fmbId;
    out.fmbTakhmeenId = data.fmbTakhmeenId ?? null;
  }
  if (data.hijriYearStart !== undefined && data.hijriYearStart !== "") {
    out.hijriYearStart = Number(data.hijriYearStart);
  }
  if (data.unitAmount !== undefined && data.unitAmount !== "") {
    out.unitAmount = Number(data.unitAmount);
  }
  if (data.isAmountOverridden && data.amount !== undefined && data.amount !== "") {
    out.amount = Number(data.amount);
  }
  const bookedSlots = transformBookedSlotsForApi(data);
  if (bookedSlots !== undefined) {
    out.bookedSlots = bookedSlots;
  }
  return out;
};

function validateContributionForm(values: Record<string, unknown>) {
  return {
    ...validateContributionFmbOrPeriod(values),
    ...validateBeneficiaryDisplayName(values),
    ...validatePositiveContributionTotal(values),
    ...validateZabihatBookedSlots(values),
  };
}

function EnsureCategoryId() {
  const record = useRecordContext();
  const { control, setValue, getValues } = useFormContext();
  const categoryId = useWatch({ control, name: "categoryId" });
  const { data: categories } = useGetList<{
    id: string;
    code: string;
    defaultUnitAmount?: number | null;
  }>("fmbContributionCategory", {
    filter: { isActive: true },
    sort: { field: "sortOrder", order: "ASC" },
    pagination: { page: 1, perPage: 100 },
  });

  useEffect(() => {
    if (!categoryId && categories && categories.length > 0) {
      const type = record?.contributionType || getValues("contributionType") || "ZABIHAT";
      const matched = categories.find((c) => c.code === type) || categories[0];
      setValue("categoryId", matched.id, { shouldDirty: false });
      setValue("contributionType", matched.code === "ZABIHAT" ? "ZABIHAT" : "VOLUNTARY", {
        shouldDirty: false,
      });
    }
  }, [categoryId, categories, record?.contributionType, getValues, setValue]);

  return null;
}

function CategorySync() {
  const { control, setValue } = useFormContext();
  const categoryId = useWatch({ control, name: "categoryId" });
  const prevCategoryIdRef = useRef<string | undefined>(categoryId);
  const { data: category } = useGetOne<{
    id: string;
    code: string;
    defaultUnitAmount?: number | null;
  }>("fmbContributionCategory", { id: categoryId }, { enabled: Boolean(categoryId) });

  useEffect(() => {
    if (!category) return;
    const isInitial = prevCategoryIdRef.current === undefined;
    const hasChanged = prevCategoryIdRef.current !== categoryId;
    prevCategoryIdRef.current = categoryId;

    const nextType = category.code === "ZABIHAT" ? "ZABIHAT" : "VOLUNTARY";
    setValue("contributionType", nextType, { shouldDirty: false });

    if (
      !isInitial &&
      hasChanged &&
      category.defaultUnitAmount != null &&
      category.defaultUnitAmount > 0
    ) {
      setValue("unitAmount", category.defaultUnitAmount, { shouldDirty: true });
    }
  }, [category, categoryId, setValue]);

  return null;
}

export default function FmbContributionsEdit(props: EditProps) {
  return (
    <Edit {...props} transform={transform} mutationMode="pessimistic">
      <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 760 }} validate={validateContributionForm}>
        <EnsureCategoryId />
        <CategorySync />
        <ClearTakhmeenWhenNoFmb />
        <TakhmeenHijriSync />
        <SeedOverrideAmountFromCalculated />
        <Grid container spacing={2}>
          <Grid size={12}>
            <ReferenceInput source="fmbId" reference="fmbData" perPage={100}>
              <ITSInput
                label="FMB record (optional)"
                optionText={(r) => `${r.fileNo ?? "—"} · ITS ${r.itsNo ?? "—"}`}
                fullWidth
                debounce={300}
                filterToQuery={(q) => ({ search: q })}
                helperText={false}
              />
            </ReferenceInput>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="name" label="Account name" fullWidth disabled />
          </Grid>
          <FormDataConsumer>
            {({ formData }) =>
              formData?.fmbId ? (
                <Grid size={12}>
                  <ReferenceInput
                    source="fmbTakhmeenId"
                    reference="fmbTakhmeen"
                    filter={{ fmbId: formData.fmbId }}
                    perPage={200}
                    helperText={false}
                  >
                    <AutocompleteInput
                      fullWidth
                      label="Annual period (takhmeen)"
                      optionText={(choice) =>
                        `${choice.hijriYearStart ?? "—"}–${choice.hijriYearEnd ?? "—"} · ${formatINR(
                          choice.takhmeenAmount,
                          { empty: "—" }
                        )}`
                      }
                      shouldRenderSuggestions={(val: string) => val.trim().length >= 0}
                      noOptionsText="No annual periods for this FMB"
                      helperText={false}
                    />
                  </ReferenceInput>
                </Grid>
              ) : null
            }
          </FormDataConsumer>
          <FmbHouseholdSummaryAndItsSync />
          <Grid size={{ xs: 12, sm: 3 }}>
            <ReferenceInput
              source="categoryId"
              reference="fmbContributionCategory"
              filter={{ isActive: true }}
              sort={{ field: "sortOrder", order: "ASC" }}
              perPage={100}
            >
              <SelectInput
                optionText="name"
                label="Category"
                fullWidth
                validate={[required()]}
                helperText={false}
              />
            </ReferenceInput>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <NoArrowKeyNumberInput
              source="hijriYearStart"
              label="Hijri year start"
              fullWidth
              helperText="Required if no FMB record"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <BeneficiaryItsAutocomplete />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput
              source="beneficiaryName"
              label="Beneficiary name"
              fullWidth
              helperText="Auto-filled from ITS directory when available. Enter manually if ITS is not in directory."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <NoArrowKeyNumberInput
              source="quantity"
              label="Quantity"
              fullWidth
              validate={[required(), minValue(1)]}
              helperText={false}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <NoArrowKeyNumberInput
              source="unitAmount"
              label="Unit amount"
              fullWidth
              helperText={false}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <CalculatedTotalAmountField />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <BooleanInput
              source="isAmountOverridden"
              label="Override total amount"
              helperText={false}
            />
          </Grid>
          <FormDataConsumer>
            {({ formData }) =>
              formData?.isAmountOverridden ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <NoArrowKeyNumberInput
                    source="amount"
                    label="Amount (override)"
                    fullWidth
                    validate={[required(), minValue(1)]}
                    helperText={false}
                  />
                </Grid>
              ) : null
            }
          </FormDataConsumer>
          <Grid size={12}>
            <TextInput source="remarks" fullWidth multiline minRows={3} />
          </Grid>
          <Grid size={12}>
            <FmbZabihatBookedSlotsInput />
          </Grid>
        </Grid>
      </SimpleForm>
    </Edit>
  );
}

/**
 * Reflects the **current** FMB selection from the form (not only the loaded record).
 * Renders FMB number + HOF ITS; pre-fills beneficiary HOF ITS when that field is empty.
 */
function FmbHouseholdSummaryAndItsSync() {
  const { control, setValue, getValues } = useFormContext();
  const fmbId = useWatch({ control, name: "fmbId" });
  const { data: fmb } = useGetOne("fmbData", { id: fmbId }, { enabled: Boolean(fmbId) });

  useEffect(() => {
    if (!fmbId) {
      setValue("name", "", { shouldDirty: false, shouldTouch: false });
    }
  }, [fmbId, setValue]);

  useEffect(() => {
    if (!fmbId || fmb?.name == null) return;
    const n = String(fmb.name).trim();
    if (n) {
      setValue("name", n, { shouldDirty: false, shouldTouch: false });
    }
  }, [fmb?.name, fmbId, setValue]);

  useEffect(() => {
    const raw = fmb?.itsNo;
    if (raw == null || raw === "") return;
    const hofIts = String(raw).trim();
    if (!hofIts) return;
    const b = getValues("beneficiaryItsNo");
    const bEmpty = b == null || String(b).trim() === "";
    if (bEmpty) {
      setValue("beneficiaryItsNo", hofIts, { shouldDirty: false, shouldTouch: false });
      const accountName =
        fmb?.name != null && String(fmb.name).trim() !== "" ? String(fmb.name).trim() : "";
      const currentBn = getValues("beneficiaryName");
      if (accountName && (currentBn == null || String(currentBn).trim() === "")) {
        setValue("beneficiaryName", accountName, { shouldDirty: false, shouldTouch: false });
      }
    }
  }, [fmb, fmbId, getValues, setValue]);

  if (!fmbId) {
    return (
      <Grid size={12}>
        <TextField
          label="FMB link"
          value="Standalone — beneficiary ITS + Hijri period (no FMB record)"
          fullWidth
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
    );
  }

  return (
    <>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="File no."
          value={fmb?.fileNo != null && fmb.fileNo !== "" ? String(fmb.fileNo) : ""}
          fullWidth
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          label="HOF ITS"
          value={fmb?.itsNo != null && fmb.itsNo !== "" ? String(fmb.itsNo) : ""}
          fullWidth
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
    </>
  );
}

function CalculatedTotalAmountField() {
  const { control } = useFormContext();
  const quantity = useWatch({ control, name: "quantity" });
  const unitAmount = useWatch({ control, name: "unitAmount" });

  const q = Number(quantity) || 0;
  const u = Number(unitAmount) || 0;
  const total = q * u;

  return (
    <TextField
      label="Calculated total amount"
      value={total}
      fullWidth
      disabled
      inputProps={{ inputMode: "numeric" }}
    />
  );
}
